/**
 * When a picture board says the word behind its banner picture (specs/picture-word-mode.md §2.2).
 *
 * This lives outside BoardScene on purpose: the delay IS the design — spoken instantly, the audio
 * lands before the child has looked at the picture and they stop looking — but Phaser cannot be
 * imported in the node test environment (see vitest.config.ts), so anything left inside the scene
 * is untestable. Keeping the timing and the word lookup here lets src/game/pictureSpeak.test.ts
 * pin both without booting a game.
 */
import type { PackWord } from '@/data/packs/types';

export const PICTURE_SPEAK_DELAY_MS = 300;

/** The slice of Phaser's `scene.time` this needs — so tests can pass a plain recorder. */
export interface SpeakTimer {
  delayedCall(ms: number, cb: () => void): unknown;
}

/**
 * The word a banner icon id stands for, or null when this board carries no such word (the queue
 * is spent — `iconId` is then `''` — or the icon belongs to another set). Words are keyed on
 * `id`, falling back to the surface form for pools whose words carry no id.
 */
export function pictureWordText(iconId: string | undefined, words: readonly PackWord[]): string | null {
  if (!iconId) return null;
  const word = words.find((w) => (typeof w.id === 'string' ? w.id : w.text) === iconId);
  return word ? word.text : null;
}

/**
 * Schedule the readout of the pictured word, PICTURE_SPEAK_DELAY_MS after the picture lands.
 * Returns whether anything was scheduled — false means there was nothing to say, not a failure.
 */
export function schedulePictureSpeech(
  timer: SpeakTimer,
  iconId: string | undefined,
  words: readonly PackWord[],
  speak: (text: string) => void,
): boolean {
  const text = pictureWordText(iconId, words);
  if (text === null) return false;
  timer.delayedCall(PICTURE_SPEAK_DELAY_MS, () => speak(text));
  return true;
}
